/**
 * @module useSuppliers
 * @description React Query hooks for supplier management
 * 
 * Provides hooks for:
 * - Supplier listing and management
 * - Supplier performance tracking
 * - Purchase order management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetch suppliers list
 * @param {Object} filters - Supplier filters
 * @returns {Object} Query result with suppliers
 * 
 * @example
 * const { data: suppliers } = useSuppliers({ active: true });
 */
export const useSuppliers = (filters = {}) => {
  return useQuery({
    queryKey: ['suppliers', filters],
    queryFn: async () => {
      const { data } = await api.get(API_ENDPOINTS.SUPPLIERS, { params: filters });
      return data;
    }
  });
};

/**
 * Fetch single supplier by ID
 * @param {string|number} supplierId - Supplier ID
 * @returns {Object} Query result with supplier details
 * 
 * @example
 * const { data: supplier } = useSupplier(supplierId);
 */
export const useSupplier = (supplierId) => {
  return useQuery({
    queryKey: ['suppliers', supplierId],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.SUPPLIERS}/${supplierId}`);
      return data;
    },
    enabled: !!supplierId
  });
};

/**
 * Create a new supplier
 * @returns {Object} Mutation object
 * 
 * @example
 * const createMutation = useCreateSupplier();
 * createMutation.mutate({ name: 'Supplier Co', contact: 'contact@supplier.com' });
 */
export const useCreateSupplier = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (supplierData) => {
      const { data } = await api.post(API_ENDPOINTS.SUPPLIERS, supplierData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });
};

/**
 * Update a supplier
 * @returns {Object} Mutation object
 * 
 * @example
 * const updateMutation = useUpdateSupplier();
 * updateMutation.mutate({ supplierId: 1, data: { active: false } });
 */
export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ supplierId, data }) => {
      const { data: response } = await api.put(`${API_ENDPOINTS.SUPPLIERS}/${supplierId}`, data);
      return response;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers', variables.supplierId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });
};

/**
 * Delete a supplier
 * @returns {Object} Mutation object
 * 
 * @example
 * const deleteMutation = useDeleteSupplier();
 * deleteMutation.mutate(supplierId);
 */
export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (supplierId) => {
      const { data } = await api.delete(`${API_ENDPOINTS.SUPPLIERS}/${supplierId}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });
};

/**
 * Fetch supplier performance stats
 * @param {string|number} supplierId - Supplier ID
 * @returns {Object} Query result with performance data
 * 
 * @example
 * const { data: performance } = useSupplierPerformance(supplierId);
 */
export const useSupplierPerformance = (supplierId) => {
  return useQuery({
    queryKey: ['suppliers', supplierId, 'performance'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.SUPPLIERS}/${supplierId}/performance`);
      return data;
    },
    enabled: !!supplierId
  });
};

/**
 * Fetch supplier system status
 * @returns {Object} Query result with system status data
 */
export const useSupplierStatus = () => {
  return useQuery({
    queryKey: ['suppliers', 'status'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.SUPPLIERS}/status`);
      return data;
    },
  });
};

/**
 * Fetch supplier orders
 * @returns {Object} Query result with supplier orders data
 */
export const useSupplierOrders = () => {
  return useQuery({
    queryKey: ['suppliers', 'orders'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.SUPPLIERS}/orders`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (data?.orders || []);
    },
  });
};

/**
 * Fetch supplier evaluations
 * @returns {Object} Query result with supplier evaluations data
 */
export const useSupplierEvaluations = () => {
  return useQuery({
    queryKey: ['suppliers', 'evaluations'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.SUPPLIERS}/evaluations`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (data?.evaluations || []);
    },
  });
};

/**
 * Fetch supplier payments
 * @returns {Object} Query result with supplier payments data
 */
export const useSupplierPayments = () => {
  return useQuery({
    queryKey: ['suppliers', 'payments'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.SUPPLIERS}/payments`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (data?.payments || []);
    },
  });
};

/**
 * Fetch supplier analytics
 * @returns {Object} Query result with supplier analytics data
 */
export const useSupplierAnalytics = () => {
  return useQuery({
    queryKey: ['suppliers', 'analytics'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.SUPPLIERS}/analytics`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (data?.analytics || []);
    },
  });
};
